/**
 * Service for reading and writing the model policy — which model serves which
 * translation slot — via the platform admin API.
 *
 * Three endpoints, relative to REACT_APP_FUNCTIONS_URL — which already ends in `/api` in every
 * deploy (see the Static Web Apps workflow), so the paths below must NOT repeat it:
 *  - GET  /platform/model-policy           — policy in force, its ETag, and the model catalogue
 *  - POST /platform/model-policy/validate  — check a draft without storing it
 *  - PUT  /platform/model-policy           — store, conditional on If-Match
 *
 * They used to repeat it. Every test of this panel mocked the service or answered a
 * `**\/platform/model-policy` glob, which matches `/api/api/...` just as happily, so the 404
 * every real deployment would have returned was invisible until a test read the actual URL.
 *
 * Follows pimTierApiService: raw `fetch` rather than the shared wrapper so tests
 * can mock these endpoints independently, with the bearer token attached here.
 */
import { getAuthHeader } from '../utils/jwtService';

const getBaseUrl = () => {
    const fromEnv = process.env.REACT_APP_FUNCTIONS_URL;
    if (fromEnv) {
        return fromEnv.endsWith('/') ? fromEnv.slice(0, -1) : fromEnv;
    }
    return '';
};

const jsonHeaders = () => ({
    'Content-Type': 'application/json',
    ...getAuthHeader(),
});

/**
 * Reads the policy in force.
 *
 * `source` distinguishes a stored document from the compiled default, which the
 * editor needs: with nothing stored the first write is a create, and the ETag to
 * send is `*` rather than a value.
 *
 * `aiFeatureDefaults` is what AI Summary, Q&A and Content Library translation actually run on
 * TODAY when `policy.aiFeatures` (or one of its three slots) is null — computed server-side so
 * the "AI 기능" card can show a real default instead of a blank, without that default ever being
 * written into the document just because the card was opened.
 *
 * @returns {Promise<Object>} policy, source, version, etag, supportedModels,
 *   enginePropagationSeconds, aiFeatureDefaults.
 */
export async function getModelPolicy() {
    const response = await fetch(`${getBaseUrl()}/platform/model-policy`, {
        method: 'GET',
        headers: jsonHeaders(),
    });

    if (!response.ok) {
        const error = new Error(`Failed to load the model policy (${response.status})`);
        error.status = response.status;
        throw error;
    }

    return response.json();
}

/**
 * Validates a draft without storing it, so the editor can show what is wrong
 * before the operator commits to a save.
 *
 * @param {Object} policy The candidate document.
 * @returns {Promise<{valid: boolean, violations: Array}>} The verdict and every violation.
 */
export async function validateModelPolicy(policy) {
    const response = await fetch(`${getBaseUrl()}/platform/model-policy/validate`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(policy),
    });

    if (!response.ok) {
        const error = new Error(`Validation request failed (${response.status})`);
        error.status = response.status;
        throw error;
    }

    return response.json();
}

/**
 * Stores the policy, conditional on the version the editor loaded.
 *
 * The precondition is not optional: without it a save is an unconditional
 * overwrite, and two administrators editing at once would silently lose one
 * edit. A 409 means the document moved — the caller reloads and reapplies rather
 * than retrying, because a retry would write onto a document nobody reviewed.
 *
 * @param {Object} policy The document to store.
 * @param {string} etag The ETag from the load, or `*` to create the first one.
 * @returns {Promise<Object>} policy, version, etag, enginePropagationSeconds, appliesTo.
 * @throws {Error} With `status` 409 on conflict, 400 with `violations` when invalid.
 */
export async function saveModelPolicy(policy, etag) {
    const response = await fetch(`${getBaseUrl()}/platform/model-policy`, {
        method: 'PUT',
        headers: { ...jsonHeaders(), 'If-Match': etag || '*' },
        body: JSON.stringify(policy),
    });

    if (response.ok) {
        return response.json();
    }

    let body = null;
    try {
        body = await response.json();
    } catch {
        // A non-JSON error body is still a failure; the status is what the caller
        // branches on, so an unparseable body must not mask it.
    }

    const error = new Error(body?.error || `Failed to save the model policy (${response.status})`);
    error.status = response.status;
    error.errorCode = body?.errorCode;
    error.violations = body?.violations || [];
    throw error;
}

const modelPolicyApiService = { getModelPolicy, validateModelPolicy, saveModelPolicy };
export default modelPolicyApiService;
